// layouts/DashboardLayout/components/Sidebar/Sidebar.tsx
import { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTheme } from '../../../../contexts/ThemeContext';
import DigiBrigiLogo from '../../../../components/common/DigiBrigiLogo/DigiBrigiLogo';
import styles from './Sidebar.module.css';
import { initOnboardingTour } from '../../../../components/common/OnboardingTour/OnboardingTour';

interface NavItem {
  path: string;
  icon: string;
  label: string;
  locked?: boolean;
  requiredLevel?: string;
  alwaysInactive?: boolean
}

interface SidebarProps {
  isOpen: boolean;
  onClose?: () => void;
}

const Sidebar = ({ isOpen, onClose }: SidebarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { onLogout, user } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const isFree = user?.tier?.name?.toLowerCase() === 'free';

  // V demo simulátoru (/preview/dashboard) musí odkazy zůstat uvnitř preview.
  const isPreview = window.location.pathname.startsWith('/preview/dashboard');
  const basePath = isPreview ? '/preview/dashboard' : '/dashboard';

  const navItems = useMemo<NavItem[]>(
    () => [
      { path: basePath, icon: '📊', label: 'Přehled' },
      { path: `${basePath}/bonuses`, icon: '💰', label: 'Bonusy', locked: isFree, requiredLevel: 'Basic+' },
      { path: `${basePath}/bonus-history`, icon: '📋', label: 'Historie bonusů', locked: isFree, requiredLevel: 'Basic+' },
      { path: `${basePath}/referrals`, icon: '👥', label: 'Doporučení', locked: isFree, requiredLevel: 'Basic+' },
      { path: `${basePath}/investments`, icon: '📈', label: 'Investice' },
      { path: `${basePath}/settings`, icon: '⚙️', label: 'Nastavení' },
      { path: `${basePath}`, icon: '?', label: 'Průvodce', locked: isFree, requiredLevel: 'Basic+', alwaysInactive: true },
      ...(user?.journey_done_step_ids === null ? [] : [{ path: `${basePath}/journey`, icon: '🚀', label: 'Začínáme' }]),
      ...(user?.admin ? [{ path: '/admin/screenia', icon: 'BJ', label: 'Screenia' }] : []),
      ...(user?.admin ? [{ path: '/admin/bonuses', icon: '🛠️', label: 'Správa bonusů' }] : []),
      ...(user?.admin ? [{ path: '/admin/push_notifications', icon: '🔔', label: 'Push notifikace' }] : []),
      ...(user?.admin ? [{ path: '/admin/transactions', icon: '💸', label: 'Transakce' }] : []),
      ...(user?.statistician ? [{ path: '/admin/statistics', icon: '📈', label: 'Statistiky' }] : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isFree, user, basePath],
  );

  const restartTour = () => {
    initOnboardingTour();
  };

  const handleNavClick = (item: NavItem) => {
    if (item.locked) return;
    if (window.innerWidth <= 1024) onClose?.();
    if (item.label === 'Průvodce') restartTour();
  };

  return (
    <aside className={`${styles.sidebar} ${isOpen ? styles.open : ''}`}>
      <div className={styles.top}>
        <DigiBrigiLogo size={22} onClick={() => navigate(basePath)} />
        <button onClick={onClose} className={styles.closeBtn} aria-label="Zavřít menu">
          ✕
        </button>
      </div>

      <nav className={styles.nav}>
        {navItems.map((item, index) => {
          const isActive =
            (location.pathname === item.path ||
            (item.path !== basePath && item.path !== '/admin' && location.pathname.startsWith(item.path))) && !item.alwaysInactive;
          
          

          if (item.locked) {
            return (
              <div
                key={item.label}
                className={`${styles.navItem} ${styles.locked}`}
                onClick={() => handleNavClick(item)}
                title={`Dostupné od ${item.requiredLevel} členství`}
              >
                <span className={styles.icon}>{item.icon}</span>
                <span className={styles.label}>{item.label}</span>
                <span className={styles.lockBadge}>{item.requiredLevel}</span>
              </div>
            );
          }

          return (
            <Link
              key={item.label}
              to={item.path}
              className={`${styles.navItem} ${isActive ? styles.active : ''}`}
              onClick={() => handleNavClick(item)}
              data-tour={item.path === basePath ? 'nav-overview' : `nav-${item.path.split('/').pop()}`}
            >
              <span className={styles.icon}>{item.icon}</span>
              <span className={styles.label}>{item.label}</span>
            </Link>
          );
        })}
      </nav>

      <div className={styles.bottom}>
        <button className={styles.navItem} onClick={toggleTheme}>
          <span className={styles.icon}>{theme === 'light' ? '🌙' : '☀️'}</span>
          <span className={styles.label}>Změnit téma</span>
        </button>
        <button className={styles.logoutBtn} onClick={() => onLogout()}>
          Odhlásit se
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
